import {
	Autocomplete,
	Box,
	Collapse,
	IconButton,
	LinearProgress,
	TextField,
	Typography,
} from "@mui/material";
import DataTable from "../../../../widgets/DataTable";
import ImageReel from "../../../../widgets/ImageReel/ImageReel";
import { useState, useEffect, useRef } from "react";
import ideasServices from "../../../../services/ideasServices";
import hospitalsServices from "../../../../services/hospitalsServices";
import {
	ChevronDown,
	ChevronLeft,
	ChevronRight,
	ChevronUp,
	ImagePlus,
	PenLine,
	Search,
	X,
} from "lucide-react";
import Skeleton from "@mui/material/Skeleton";
import SuccessCaseImageModal from "../../../../widgets/SuccessCaseImageModal/SuccessCaseImageModal";
import { FileUploader } from "react-drag-drop-files";
import QuestionMark from "../../../../../src/assets/question_mark.png";
import "./IdeasView.scss";
import { DropdownFilter } from "../../../../widgets/DropdownFilter/DropdownFilter";
import CustomSnackbar from "../../Snackbar/SnackBar";

interface IdeasViewsProps {
  updateContent: (menu: string) => void;
  onSend: (data: any) => void;
}

export default function IdeasViews({ updateContent, onSend }: IdeasViewsProps) {
  const [successCases, setSuccessCases] = useState<any[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [openMarkSuccessModal, setOpenMarkSuccessModal] = useState(false);
  const [openUnmarkSuccessModal, setOpenUnmarkSuccessModal] = useState(false);
  const [isImageLoading, setIsImageLoading] = useState(false);
  const [preview, setPreview] = useState<string | null>(null);
  const [progress, setProgress] = useState<number>(50);
  const [file, setFile] = useState<File>();
  const [successCaseIdea, setSuccessCaseIdea] = useState<any>(null);
  const [ideaTitles, setIdeaTitles] = useState<Array<any>>([]);
  const [searchInput, setSearchInput] = useState<string>();
  const [show, setShow] = useState(true);
  const [ideas, setIdeas] = useState<Array<any>>([]);
  const [visibleIdeas, setVisibleIdeas] = useState<Array<any>>([]);
  const [selectedUnits, setSelectedUnits] = useState<any[]>([]);
  const [selectedAreas, setSelectedAreas] = useState<any[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<any[]>([]);
  const [selectedStartDate, setSelectedStartDate] = useState<Date>();
  const [selectedFinishDate, setSelectedFinishDate] = useState<Date>();
  const [hospitalAreaOptions, setHospitalAreaOptions] = useState<any[]>([]);
  const [areaOptions, setAreaOptions] = useState<any[]>([]);
  const [categoryOptions, setCategoryOptions] = useState<any[]>([]);
  const [menuState, setMenuState] = useState<{
    id: string | null;
    anchorEl: HTMLElement | null;
  }>({ id: null, anchorEl: null });
  const [openAddedSnackbar, setOpenAddedSnackbar] = useState(false);
  const [openDeletedSnackbar, setOpenDeletedSnackbar] = useState(false);
  const [snackbarAddedMode, setSnackbarAddedMode] = useState("");
  const [snackbarDeletedMode, setSnackbarDeletedMode] = useState("");

  const ideasHeaders = [
    {
      name: "Nombre de la idea",
      id: "title",
    },
    {
      name: "Estatus",
      id: "status",
    },
    {
      name: "Unidad",
      id: "hospitalUnits",
    },
    {
      name: "Área",
      id: "hospitalAreas",
    },
    {
      name: "Autor(es)",
      id: "authorsNames",
    },
  ];
  const defaultImage = "../../../../assets/success_cases_bg.jpg";

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      await ideasServices.getAllIdeas().then((res) => formatIdeas(res));
    };
    fetchData();
  }, []);

  function formatIdeas(ideas: any) {
    const ideaTitles = ideas.map((idea: any) => idea.title);
    const formattedIdeas = ideas.map((idea: any) => ({
      ...idea,
      authorsNames: idea.authors
        .map((author: any) => `${author.first_name} ${author.last_name}`)
        .join(", "),
      hospitalUnits: idea.hospital_units
        .map((unit: any) => unit.name)
        .join(", "),
      hospitalAreas: idea.hospital_areas
        .map((area: any) => area.area_name)
        .join(", "),
      actions: [<PenLine />],
    }));
    setIdeas(formattedIdeas);
    setIdeaTitles(ideaTitles);
    filterSuccessSCases(formattedIdeas);
    setIsLoading(false);
  }

  function filterSuccessSCases(ideas: any) {
    const successCasesIdeas = ideas.filter((idea: any) => idea.is_success_case);
    setSuccessCases(successCasesIdeas);
  }

  const handleRowClick = (rowData: any) => {
    updateContent("idea-details");
    onSend(rowData);
  };

  function handleSuccessCaseIconClick(idea: any) {
    setSuccessCaseIdea(idea);
  }

  useEffect(() => {
    if (successCaseIdea) {
      !successCaseIdea.is_success_case && setOpenMarkSuccessModal(true);
      successCaseIdea.is_success_case && setOpenUnmarkSuccessModal(true);
    }
  }, [successCaseIdea]);

  const handleChange = (file: File) => {
    setFile(file);
    const reader = new FileReader();
    setIsImageLoading(true);
    setProgress(0);

    reader.onprogress = (e) => {
      if (e.lengthComputable) {
        const percent = Math.round((e.loaded / e.total) * 100);
        setProgress(percent);
      }
    };

    reader.onloadend = () => {
      setPreview(reader.result as string);
      setIsImageLoading(false);
    };

    reader.readAsDataURL(file);
  };

  const handleMarkCancel = () => {
    setOpenMarkSuccessModal(false);
    setFile(undefined);
    setSuccessCaseIdea(null);
  };

  const handleMarkSave = async () => {
    if (file) {
      await ideasServices
        .markSuccessIdea(file, successCaseIdea.idea_id)
        .then((res) => {
          setOpenMarkSuccessModal(false);
        })
        .finally(() => setOpenAddedSnackbar(true));
    }
    setFile(undefined);
    updateIdeas();
  };

  const handleUnmarkCancel = () => {
    setOpenUnmarkSuccessModal(false);
    setFile(undefined);
    setSuccessCaseIdea(null);
  };

  const handleUnmarkSave = async () => {
    setIsImageLoading(true);
    await ideasServices
      .unmarkSuccessIdea(successCaseIdea.idea_id)
      .then((res) => {
        setOpenUnmarkSuccessModal(false);
        setIsImageLoading(false);
      })
      .finally(() => setOpenDeletedSnackbar(true));
    updateIdeas();
  };

  const updateIdeas = () => {
    const updatedIdeas = ideas.map((idea) =>
      idea.idea_id === successCaseIdea.idea_id
        ? { ...idea, is_success_case: !idea.is_success_case }
        : idea
    );
    setIdeas(updatedIdeas);
    setSuccessCaseIdea(null);
    updateSuccessCases(updatedIdeas);
  };

  const updateSuccessCases = (updatedIdeas: any[]) => {
    const successCases = updatedIdeas.filter((idea) => idea.is_success_case);
    setSuccessCases(successCases);
    setSuccessCaseIdea(null);
  };

  useEffect(() => {
    const filtered = ideas.filter((idea) => {
      const matchesTitle =
        !searchInput?.trim() ||
        idea.title.toLowerCase().includes(searchInput.toLowerCase());

      const matchesUnits =
        selectedUnits.length === 0 ||
        selectedUnits.some((unit) =>
          idea.hospital_units.some((h: any) => h.id === unit.value)
        );

      const matchesAreas =
        selectedAreas.length === 0 ||
        selectedAreas.some((area) =>
          idea.hospital_areas.some(
            (a: any) => a.hospital_area_id === area.value
          )
        );

      const matchesCategories =
        selectedCategories.length === 0 ||
        selectedCategories.some(
          (category) => category.value === idea.category_id
        );

      const ideaDate = new Date(idea.created_at);
      const matchesStartDate =
        !selectedStartDate || ideaDate >= selectedStartDate;

      const matchesFinishDate =
        !selectedFinishDate || ideaDate <= selectedFinishDate;

      return (
        matchesTitle &&
        matchesUnits &&
        matchesAreas &&
        matchesCategories &&
        matchesStartDate &&
        matchesFinishDate
      );
    });
    setVisibleIdeas(filtered);
  }, [
    searchInput,
    selectedUnits,
    selectedAreas,
    selectedCategories,
    selectedStartDate,
    selectedFinishDate,
    ideas,
  ]);

  useEffect(() => {
    getAllHospitalAreas();
    getAllAreas();
    getAllCategories();
  }, []);

  const getAllHospitalAreas = () => {
    hospitalsServices.getAllHospitals().then((hospitalAreas) => {
      let aux = [];
      for (let hospitalArea of hospitalAreas) {
        aux.push({
          label: hospitalArea["hospital_name"],
          value: hospitalArea["id"],
        });
      }
      setHospitalAreaOptions(aux);
    });
  };

  const getAllAreas = () => {
    hospitalsServices.getAllActiveAreas().then((Areas) => {
      let aux = [];
      for (let Area of Areas) {
        aux.push({
          label: Area["area_name"],
          value: Area["id"],
          hospital_unit_id: Area["hospital_id"],
        });
      }
      setAreaOptions(aux);
    });
  };

  const handleOpen = (id: string) => (e: React.MouseEvent<HTMLButtonElement>) =>
    setMenuState({ id, anchorEl: e.currentTarget });

  const handleClose = () => setMenuState({ id: null, anchorEl: null });

  const getAllCategories = () => {
    ideasServices.getAllCategories().then((categories) => {
      let aux = [];
      for (let category of categories) {
        aux.push({
          label: category["category"],
          value: category["id"],
        });
      }
      setCategoryOptions(aux);
    });
  };

  const filteredAreaOptions = areaOptions;

  const filteredHospitalOptions = selectedAreas.length
    ? hospitalAreaOptions.filter((unit) =>
        selectedAreas.some((area) =>
          areaOptions.find(
            (a) => a.value === area.value && a.hospital_unit_id === unit.value
          )
        )
      )
    : hospitalAreaOptions;

  const getImageFileName = (file_route: any) => {
    if (file_route) {
      const routeParts = file_route.split("/");
      const fileName = routeParts[routeParts.length - 1];
      return file_route ? fileName : "Default";
    } else {
      return "Default";
    }
  };

  const reelRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (reelRef && reelRef.current?.scrollWidth !== undefined) {
      if (reelRef.current) {
        const scrollAmount = 23.8125 * 16 + 1;
        reelRef.current.scrollBy({
          left: direction === "left" ? -scrollAmount : scrollAmount,
          behavior: "smooth",
        });
      }
      reelRef.current.addEventListener("scrollend", () => {
        toggleReelArrows(reelRef);
      });
    }
  };

  const toggleReelArrows = (reelRef: any) => {
    const reelLeftArrow = document.querySelector<HTMLElement>(".arrowLeft");
    const reelRightArrow = document.querySelector<HTMLElement>(".arrowRight");

    if (reelRef.current.scrollLeft <= 0) {
      reelLeftArrow?.classList.add("hidden");
    } else {
      reelLeftArrow?.classList.remove("hidden");
    }

    if (
      reelRef.current.scrollLeft >=
      reelRef.current.scrollWidth - reelRef.current.clientWidth
    ) {
      reelRightArrow?.classList.add("hidden");
    } else {
      reelRightArrow?.classList.remove("hidden");
    }
  };

  return (
    <>
      <Box>
        <Box className="tableLabel">Casos de éxito</Box>
        <Box className="succesCasesReel">
          {successCases.length > 0 ? (
            <>
              <ChevronLeft
                className="reelContainer__icon arrowLeft hidden"
                onClick={() => scroll("left")}
              />
              <ImageReel
                data={successCases}
                clickEvent={handleRowClick}
                successCaseClick={handleSuccessCaseIconClick}
                reelRef={reelRef}
              />
              <ChevronRight
                className="reelContainer__icon arrowRight"
                onClick={() => scroll("right")}
              />
            </>
          ) : (
            <Box className="reel-skeleton">
              {Array.from({ length: 4 }).map((_, index) => (
                <Box className="reel-skeleton__card">
                  <Skeleton
                    className="reel-skeleton__card__image"
                    variant="rectangular"
                    animation="wave"
                  />
                  <Skeleton
                    className="reel-skeleton__card__text"
                    animation="wave"
                  />
                  <Skeleton
                    className="reel-skeleton__card__text--2"
                    animation="wave"
                  />
                  <Skeleton
                    className="reel-skeleton__card__text--3"
                    animation="wave"
                  />
                </Box>
              ))}
            </Box>
          )}
        </Box>
        <Box className="searchContainer">
          <Box className="searchContainer__searchbar">
            <Box>
              <Search className="header__searching__search-bar__search-icon" />
            </Box>
            <Autocomplete
              freeSolo
              forcePopupIcon={false}
              fullWidth
              options={ideaTitles}
              value={searchInput}
              inputValue={searchInput ?? ""}
              onChange={(e, newVal) => {
                setSearchInput(newVal);
              }}
              onInputChange={(e, newInput, reason) => {
                if (reason === "clear") {
                  setSearchInput("");
                } else {
                  setSearchInput(newInput);
                }
              }}
              renderInput={(params) => (
                <TextField
                  value={searchInput}
                  placeholder={"Buscar"}
                  {...params}
                />
              )}
            />
            <IconButton
              onClick={() => {
                if (show === false) {
                  setShow(!show);
                } else {
                  setShow(!show);
                }
              }}
              disableRipple
            >
              {show ? (
                <ChevronUp className="header__searching__search-bar__collapse-icon" />
              ) : (
                <ChevronDown className="header__searching__search-bar__collapse-icon" />
              )}
            </IconButton>
          </Box>
          <Collapse in={show}>
            <Box className="searchContainer__filters">
              <DropdownFilter
                id="unit"
                type="options"
                label="Unidad"
                options={filteredHospitalOptions}
                selected={selectedUnits}
                onChange={setSelectedUnits}
                anchorEl={menuState.anchorEl}
                open={menuState.id === "unit"}
                onOpen={handleOpen}
                onClose={handleClose}
              />
              <DropdownFilter
                id="area"
                type="options"
                label="Área"
                options={filteredAreaOptions}
                selected={selectedAreas}
                onChange={setSelectedAreas}
                anchorEl={menuState.anchorEl}
                open={menuState.id === "area"}
                onOpen={handleOpen}
                onClose={handleClose}
              />
              <DropdownFilter
                id="category"
                type="options"
                label="Categoría"
                options={categoryOptions}
                selected={selectedCategories}
                onChange={setSelectedCategories}
                anchorEl={menuState.anchorEl}
                open={menuState.id === "category"}
                onOpen={handleOpen}
                onClose={handleClose}
              />
              <DropdownFilter
                id="start"
                type="date"
                label="Fecha de inicio"
                selectedDate={selectedStartDate}
                onDateChange={setSelectedStartDate}
                anchorEl={menuState.anchorEl}
                open={menuState.id === "start"}
                onOpen={handleOpen}
                onClose={handleClose}
              />
              <DropdownFilter
                id="end"
                type="date"
                label="Fecha de fin"
                selectedDate={selectedFinishDate}
                onDateChange={setSelectedFinishDate}
                anchorEl={menuState.anchorEl}
                open={menuState.id === "end"}
                onOpen={handleOpen}
                onClose={handleClose}
              />
            </Box>
          </Collapse>
        </Box>
        <Box className="tableLabel">Todas las ideas</Box>
        <Box className="simplified-content__body">
          <DataTable
            rows={visibleIdeas}
            headers={ideasHeaders}
            isLoading={isLoading}
            onRowClick={handleRowClick}
            tableType="ideas"
            successCaseClick={handleSuccessCaseIconClick}
          />
        </Box>
      </Box>
      <SuccessCaseImageModal
        openSuccessModal={openMarkSuccessModal}
        setOpenSuccessModal={setOpenMarkSuccessModal}
        title="¿Esta idea fue un éxito?"
        subtitle="Adjunta una imagen para mostrar esta idea en la sección de Casos de éxito."
        content={
          !isImageLoading && !file ? (
            <FileUploader handleChange={handleChange} multiple={false}>
              <Box className="sc-image-modal__content__image-container">
                <ImagePlus size={50} />
                <Typography>
                  Arrastra una imagen aquí o haz click para adjuntar un archivo.
                  <br />
                  (Dimensiones recomendadas: 1920x444px)
                </Typography>
              </Box>
            </FileUploader>
          ) : (
            <Box className="sc-image-modal__content__image-loading">
              <img
                className="sc-image-modal__content__image-loading__image"
                src={preview ?? QuestionMark}
                alt=""
              />
              <Box className="sc-image-modal__content__image-loading__info">
                <Typography className="sc-image-modal__content__image-loading__info-name">
                  {file?.name ?? "procesoadmision.png"}
                </Typography>
                <Typography className="sc-image-modal__content__image-loading__info-size">
                  {file?.size ?? "1.2 MB"}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <LinearProgress
                    className="sc-image-modal__content__image-loading__info-bar"
                    variant="determinate"
                    value={progress}
                  />
                  <Typography className="sc-image-modal__content__image-loading__info-percentage">
                    {progress}%
                  </Typography>
                </Box>
              </Box>
              <X
                className="sc-image-modal__content__image-loading__x"
                onClick={() => {
                  setFile(undefined);
                }}
              />
            </Box>
          )
        }
        nextButton="Guardar"
        disableButton={!file}
        save={handleMarkSave}
        cancel={handleMarkCancel}
      />
      <SuccessCaseImageModal
        openSuccessModal={openUnmarkSuccessModal}
        setOpenSuccessModal={setOpenUnmarkSuccessModal}
        title="¿Quitar de los casos de éxito?"
        subtitle="Se eliminará también la imagen adjunta."
        content={
          !isImageLoading ? (
            <Box className="sc-image-modal__content__image-container">
              <img
                className="sc-image-modal__content__image-container__image-thumbnail"
                src={successCaseIdea ? successCaseIdea.image_url : defaultImage}
              />
              <Box className="sc-image-modal__content__image-container__image-name">
                {successCaseIdea
                  ? getImageFileName(successCaseIdea.image_url)
                  : "Default Text"}
              </Box>
            </Box>
          ) : (
            <Box className="sc-image-modal__content__image-loading">
              <img
                className="sc-image-modal__content__image-loading__image"
                src={preview ?? QuestionMark}
                alt=""
              />
              <Box className="sc-image-modal__content__image-loading__info">
                <Typography className="sc-image-modal__content__image-loading__info-name">
                  {file?.name ?? "procesoadmision.png"}
                </Typography>
                <Typography className="sc-image-modal__content__image-loading__info-size">
                  {file?.size ?? "1.2 MB"}
                </Typography>
                <Box sx={{ display: "flex", flexDirection: "row" }}>
                  <LinearProgress
                    className="sc-image-modal__content__image-loading__info-bar"
                    variant="determinate"
                    value={progress}
                  />
                  <Typography className="sc-image-modal__content__image-loading__info-percentage">
                    {progress}%
                  </Typography>
                </Box>
              </Box>
            </Box>
          )
        }
        nextButton="Quitar"
        save={handleUnmarkSave}
        cancel={handleUnmarkCancel}
      />
      <CustomSnackbar
        open={openAddedSnackbar}
        onClose={() => setOpenAddedSnackbar(false)}
        menu="added-success-case"
        mode={snackbarAddedMode}
      />
      <CustomSnackbar
        open={openDeletedSnackbar}
        onClose={() => setOpenDeletedSnackbar(false)}
        menu="deleted-success-case"
        mode={snackbarDeletedMode}
      />
    </>
  );
}
